import { useMutation, useQuery, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { api, unwrap } from "./client";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type QuestionSummary = Schemas["QuestionSummary"];
export type QuestionDetail = Schemas["QuestionDetail"];
export type ClusterOut = Schemas["ClusterOut"];
export type JobOut = Schemas["JobOut"];
export type TransitionOut = Schemas["TransitionOut"];
export type DecisionOut = Schemas["DecisionOut"];
export type MetricsOut = Schemas["MetricsOut"];
export type EditRequest = Schemas["EditRequest"];
export type ReclusterResult = Schemas["ReclusterResult"];
export type RetryFailedResult = Schemas["RetryFailedResult"];
export type ConfigOut = Schemas["ConfigOut"];
export type DocumentStat = Schemas["DocumentStat"];
export type DocumentDetailOut = Schemas["DocumentDetailOut"];
export type DocumentTransitionOut = Schemas["DocumentTransitionOut"];
export type ProbeResult = Schemas["ProbeResult"];
export type ProbeNeighbour = Schemas["ProbeNeighbour"];
export type TaxonomyEntryOut = Schemas["TaxonomyEntryOut"];
export type PickRequest = Schemas["PickRequest"];
export type PickOut = Schemas["PickOut"];
export type PickBucket = Schemas["PickBucket"];
export type PickItem = Schemas["PickItem"];
export type PickQuestion = Schemas["PickQuestion"];
export type TaxonomyKind = "stack" | "concept" | "bucket";

/**
 * Query keys, in one place.
 *
 * Every mutation below invalidates by prefix rather than naming exact keys: approving
 * a question changes its row, the review queue, the status counts and possibly a
 * cluster, and listing those individually is how a stale table survives an action
 * that should have refreshed it.
 */
export const keys = {
  questions: ["questions"] as const,
  question: (id: number) => ["questions", id] as const,
  history: (id: number) => ["questions", id, "history"] as const,
  decisions: (id: number) => ["questions", id, "decisions"] as const,
  clusters: ["clusters"] as const,
  cluster: (canonicalId: number) => [...keys.clusters, canonicalId] as const,
  jobs: ["jobs"] as const,
  job: (id: number) => ["jobs", id] as const,
  status: ["status"] as const,
  metrics: ["metrics"] as const,
  health: ["health"] as const,
  facets: ["facets"] as const,
  documents: ["documents"] as const,
  document: (sourceDoc: string) => [...keys.documents, sourceDoc] as const,
  config: ["config"] as const,
  probe: (jobId: number) => ["probe", jobId] as const,
  taxonomy: (kind: TaxonomyKind) => ["taxonomy", kind] as const,
};

export interface QuestionFilters {
  status?: string[];
  stack_path?: string;
  primary_concept?: string;
  source_doc?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useQuestions(filters: QuestionFilters) {
  return useQuery({
    queryKey: [...keys.questions, filters],
    queryFn: async () =>
      unwrap(
        await api.GET("/questions", {
          params: {
            query: {
              status: filters.status ?? null,
              stack_path: filters.stack_path ?? null,
              primary_concept: filters.primary_concept ?? null,
              source_doc: filters.source_doc ?? null,
              search: filters.search ?? null,
              limit: filters.limit ?? 50,
              offset: filters.offset ?? 0,
            },
          },
        }),
      ),
    // A reviewer paging back and forth should not see the table blank between pages.
    placeholderData: (previous) => previous,
  });
}

export function useQuestion(id: number | null) {
  return useQuery({
    queryKey: keys.question(id ?? 0),
    queryFn: async () => unwrap(await api.GET("/questions/{qid}", { params: { path: { qid: id! } } })),
    enabled: id !== null,
  });
}

export function useHistory(id: number | null) {
  return useQuery({
    queryKey: keys.history(id ?? 0),
    queryFn: async () =>
      unwrap(await api.GET("/questions/{qid}/history", { params: { path: { qid: id! } } })),
    enabled: id !== null,
  });
}

export function useDecisions(id: number | null) {
  return useQuery({
    queryKey: keys.decisions(id ?? 0),
    queryFn: async () =>
      unwrap(await api.GET("/questions/{qid}/decisions", { params: { path: { qid: id! } } })),
    enabled: id !== null,
  });
}

export interface ClusterFilters {
  status?: string[];
  stack_path?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useClusters(filters: ClusterFilters) {
  return useQuery({
    queryKey: [...keys.clusters, filters],
    queryFn: async () =>
      unwrap(
        await api.GET("/clusters", {
          params: {
            query: {
              status: filters.status ?? null,
              stack_path: filters.stack_path ?? null,
              search: filters.search ?? null,
              limit: filters.limit ?? 25,
              offset: filters.offset ?? 0,
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * One cluster, by its canonical's id — the same assembled shape `useClusters` pages
 * through, fetched for a single question's detail view instead of a whole page of
 * them. A variant's `canonical_id` and a canonical's own `id` both work as input.
 */
export function useCluster(canonicalId: number | null) {
  return useQuery({
    queryKey: keys.cluster(canonicalId ?? 0),
    queryFn: async () =>
      unwrap(await api.GET("/clusters/{canonical_id}", { params: { path: { canonical_id: canonicalId! } } })),
    enabled: canonicalId !== null,
  });
}

/**
 * Polled lightly so the sidebar's pipeline-backlog count drains on its own while a
 * job runs — the alternative is a reviewer staring at a stale number until some
 * unrelated mutation happens to invalidate it.
 */
export function useStatusCounts() {
  return useQuery({
    queryKey: keys.status,
    queryFn: async () => unwrap(await api.GET("/status", {})),
    refetchInterval: 15_000,
  });
}

export function useMetrics() {
  return useQuery({
    queryKey: keys.metrics,
    queryFn: async () => unwrap(await api.GET("/metrics", {})),
  });
}

/**
 * The values behind the stack and concept filters.
 *
 * Long `staleTime` on purpose: these change when the bank gains a concept, which is
 * a pipeline run away, not a keystroke away. Refetching them per render would put two
 * DISTINCT scans behind every character typed into the search box.
 */
export function useFacets() {
  return useQuery({
    queryKey: keys.facets,
    queryFn: async () => unwrap(await api.GET("/questions/facets", {})),
    staleTime: 5 * 60_000,
  });
}

export function useDocuments() {
  return useQuery({
    queryKey: keys.documents,
    queryFn: async () => unwrap(await api.GET("/ingest/documents", {})),
  });
}

/** Reached a stage the pipeline runs unattended — raw chunking or extract/enrich/
 * embed/dedup. Worth polling fast: the worker may advance this within seconds. */
const AUTOMATED_QUESTION_STATUSES = ["extracted", "enriched", "embedded", "dedup_checked"] as const;

/**
 * Where one upload's live status sits, from its `DocumentStat`/`DocumentDetailOut`
 * counts — drives both `useDocument`'s poll rate and the run page's state chip.
 *
 * "review": nothing left but `pending_review`/`stale` — only a human moves this now,
 * so it is polled the way `useStatusCounts` polls the sidebar, not the way a running
 * job is. "settled": every question reached approved/duplicate/rejected/deprecated/
 * failed — nothing here changes again without a reviewer action elsewhere.
 */
export function documentPhase(
  doc: Pick<DocumentStat, "chunks" | "questions">,
): "automated" | "review" | "settled" {
  if ((doc.chunks.raw ?? 0) > 0) return "automated";
  if (AUTOMATED_QUESTION_STATUSES.some((st) => (doc.questions[st] ?? 0) > 0)) return "automated";
  if ((doc.questions.pending_review ?? 0) > 0 || (doc.questions.stale ?? 0) > 0) return "review";
  return "settled";
}

/** One upload's live pipeline status — the run page's primary poll target. */
export function useDocument(sourceDoc: string | null) {
  return useQuery({
    queryKey: keys.document(sourceDoc ?? ""),
    queryFn: async () =>
      unwrap(
        await api.GET("/ingest/documents/{source_doc}", {
          params: { path: { source_doc: sourceDoc! } },
        }),
      ),
    enabled: sourceDoc !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2_000;
      const phase = documentPhase(data);
      return phase === "automated" ? 2_000 : phase === "review" ? 15_000 : false;
    },
  });
}

/** Models, dedup config_version and what is left of today's quota (§8.6). */
export function useConfig() {
  return useQuery({
    queryKey: keys.config,
    queryFn: async () => unwrap(await api.GET("/config", {})),
    staleTime: 60_000,
  });
}

/**
 * A controlled vocabulary, read live — ARCHITECTURE §12. No staleTime: unlike
 * useFacets (a keystroke shouldn't refetch what the bank already contains), the whole
 * point of taxonomy_entries is that adding a stack or concept on the master page is
 * usable elsewhere immediately, with no restart.
 */
export function useTaxonomy(kind: TaxonomyKind, includeInactive = false) {
  return useQuery({
    queryKey: [...keys.taxonomy(kind), includeInactive],
    queryFn: async () =>
      unwrap(
        await api.GET("/taxonomy/{kind}", {
          params: { path: { kind }, query: { include_inactive: includeInactive } },
        }),
      ),
  });
}

export function useHealth() {
  return useQuery({
    queryKey: keys.health,
    queryFn: async () => unwrap(await api.GET("/health", {})),
    refetchInterval: 30_000,
  });
}

/**
 * Jobs, polled while any of them is unfinished.
 *
 * §8 invariant 01: the API returns a job id and the client polls, so this hook is the
 * client half of that contract. It stops polling once nothing is running, because a
 * bank that has been idle overnight should not be waking the browser every two
 * seconds to hear the same answer.
 */
export function useJobs(limit = 10, kind?: string) {
  return useQuery({
    queryKey: [...keys.jobs, limit, kind ?? null],
    queryFn: async () => unwrap(await api.GET("/jobs", { params: { query: { limit, kind: kind ?? null } } })),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return items.some((j) => j.status === "queued" || j.status === "running") ? 2_000 : false;
    },
  });
}

// ---------------------------------------------------------------- mutations

/** Everything a review action can change. See the note on `keys`. */
function useReviewInvalidation() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: keys.questions });
    void client.invalidateQueries({ queryKey: keys.clusters });
    void client.invalidateQueries({ queryKey: keys.status });
  };
}

export function useApprove(): UseMutationResult<void, Error, { id: number; note?: string }> {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, note }) => {
      const { error } = await api.POST("/questions/{qid}/approve", {
        params: { path: { qid: id } },
        body: { note: note ?? null },
      });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: invalidate,
  });
}

export function useReject(): UseMutationResult<void, Error, { id: number; note?: string }> {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, note }) => {
      const { error } = await api.POST("/questions/{qid}/reject", {
        params: { path: { qid: id } },
        body: { note: note ?? null },
      });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: invalidate,
  });
}

/** `stage` forces the target explicitly — "extracted" is a re-enrichment request. Left
 * unset, the backend auto-detects the first stage the row has not completed. */
export function useRequeue() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, note, stage }: { id: number; note?: string; stage?: string | undefined }) =>
      unwrap(
        await api.POST("/questions/{qid}/requeue", {
          params: { path: { qid: id } },
          body: { note: note ?? null, stage: stage ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useEdit() {
  const invalidate = useReviewInvalidation();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: EditRequest }) => {
      const { error } = await api.PATCH("/questions/{qid}", {
        params: { path: { qid: id } },
        body: patch,
      });
      if (error) throw new Error(JSON.stringify(error));
    },
    onSuccess: (_data, { id }) => {
      invalidate();
      void client.invalidateQueries({ queryKey: keys.question(id) });
      void client.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useMerge() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, otherId, note }: { id: number; otherId: number; note?: string }) =>
      unwrap(
        await api.POST("/questions/{qid}/merge", {
          params: { path: { qid: id } },
          body: { other_id: otherId, note: note ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUnmerge() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, otherId, note }: { id: number; otherId: number; note?: string }) =>
      unwrap(
        await api.POST("/questions/{qid}/unmerge", {
          params: { path: { qid: id } },
          body: { other_id: otherId, note: note ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useSoftDelete() {
  const invalidate = useReviewInvalidation();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      unwrap(
        await api.DELETE("/questions/{qid}", {
          params: { path: { qid: id } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useIngest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      force,
      expectedStack,
    }: {
      file: File;
      force: boolean;
      expectedStack: string;
    }) => {
      // openapi-fetch serialises multipart from a plain object; the generated body
      // type is what keeps the field names honest.
      const { data, error } = await api.POST("/ingest", {
        body: { file: file as unknown as string, force, expected_stack: expectedStack },
        bodySerializer: () => {
          const form = new FormData();
          form.append("file", file);
          form.append("force", String(force));
          form.append("expected_stack", expectedStack);
          return form;
        },
      });
      if (error) throw new Error(JSON.stringify(error));
      if (!data) throw new Error("The API returned no body");
      return data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.jobs });
      void client.invalidateQueries({ queryKey: keys.status });
      // A new document means new chunk rows, so the "documents seen" table is stale.
      void client.invalidateQueries({ queryKey: keys.documents });
    },
  });
}

export function useSubmitJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (kind: string) =>
      unwrap(await api.POST("/jobs/{kind}", { params: { path: { kind } } })),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.jobs }),
  });
}

export function useCancelJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) =>
      unwrap(await api.POST("/jobs/{job_id}/cancel", { params: { path: { job_id: jobId } } })),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.jobs }),
  });
}

/** CLI's `qbank retry-failed` (DESIGN §10.2), from the Questions screen: resets failed
 * questions/chunks below max_retries to the status they failed from. No model call —
 * reprocessing them still needs a pipeline run, which is a separate "Run pipeline". */
export function useRetryFailed() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/pipeline/retry-failed", {})),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.questions });
      void client.invalidateQueries({ queryKey: keys.status });
    },
  });
}

/**
 * The similarity probe — DESIGN §14.4.
 *
 * Two hooks because it is two round trips: invariant 01 means even an interactive
 * tool goes through the queue, so submitting returns a job id and the result arrives
 * on a poll. The poll is faster than the job list's because someone is watching it,
 * and it stops the moment the job reaches a terminal status.
 */
export function useSubmitProbe() {
  return useMutation({
    mutationFn: async ({ text, k }: { text: string; k: number }) =>
      unwrap(await api.POST("/probe", { body: { text, k } })),
  });
}

export function useProbe(jobId: number | null) {
  return useQuery({
    queryKey: keys.probe(jobId ?? 0),
    queryFn: async () => unwrap(await api.GET("/probe/{job_id}", { params: { path: { job_id: jobId! } } })),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;
      return status === "queued" || status === "running" ? 1_000 : false;
    },
  });
}

// ---------------------------------------------------------------- taxonomy (ARCHITECTURE §12)

function useTaxonomyInvalidation(kind: TaxonomyKind) {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: keys.taxonomy(kind) });
}

export function useCreateTaxonomyEntry(kind: TaxonomyKind) {
  const invalidate = useTaxonomyInvalidation(kind);
  return useMutation({
    mutationFn: async ({ path, note, bucket }: { path: string; note?: string; bucket?: string }) =>
      unwrap(
        await api.POST("/taxonomy/{kind}", {
          params: { path: { kind } },
          body: { path, note: note ?? null, bucket: bucket ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateTaxonomyEntry(kind: TaxonomyKind) {
  const invalidate = useTaxonomyInvalidation(kind);
  return useMutation({
    mutationFn: async ({
      id,
      path,
      note,
      bucket,
    }: {
      id: number;
      path?: string;
      note?: string;
      bucket?: string;
    }) =>
      unwrap(
        await api.PATCH("/taxonomy/{kind}/{entry_id}", {
          params: { path: { kind, entry_id: id } },
          body: { path: path ?? null, note: note ?? null, bucket: bucket ?? null },
        }),
      ),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------- Engine 2, the picker (DESIGN §17)

/**
 * A pick writes nothing (§17.1), so unlike every other mutation here this one invalidates
 * no cache — there is no server state for it to have changed.
 *
 * The key is passed per call rather than held anywhere: /pick is the one authenticated
 * route, its caller is the external Interviewer engine, and this console is standing in for
 * that caller. Persisting the secret in the browser would be a worse trade than typing it.
 */
export function usePick() {
  return useMutation({
    mutationFn: async ({ apiKey, ...body }: PickRequest & { apiKey: string }) =>
      unwrap(
        await api.POST("/pick", {
          headers: { "X-API-Key": apiKey },
          body,
        }),
      ),
  });
}

export function useDeactivateTaxonomyEntry(kind: TaxonomyKind) {
  const invalidate = useTaxonomyInvalidation(kind);
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      unwrap(
        await api.DELETE("/taxonomy/{kind}/{entry_id}", {
          params: { path: { kind, entry_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}
